import { ServerConfig } from "@/config/config";
import { Job } from "@/models/job";
import { AuthService } from "./auth-service";
import { MainSocketService } from "./main-socket-service";
import { Axios } from "@/utils/axios";
import { EventEmitter } from "@/utils/event-emitter";
import { IInitAppResponse } from "./init-service";

export interface IJobQueue {
	queuedJobs:number;
	myJobs:Job[];
}
export class JobServiceClass extends EventEmitter {
	private jobQueues:{[key:string]:IJobQueue} = {};
	private readonly url = ServerConfig.host + "/jobs";
	private readonly socketUrl = "/jobs/";
	private initialized:boolean = false;

	// Haalt de wachtrijen opnieuw op bij de server en vervangt de lokale staat (tellers en eigen taken).
	async fetchJobs(): Promise<void> {
		let result = await Axios.get(this.url);
		let queues:{[key:string]:IJobQueue} = {};
		for (let key of Object.keys(result.data)){
			queues[key] = {
				queuedJobs: result.data[key].JobCount,
				myJobs: result.data[key].MyJobs.map((j:any)=>new Job(j))
			};
			let myJobIsRunning = result.data[key].MyJobIsRunning || result.data.MyJobIsRunning;
			if (myJobIsRunning && queues[key].myJobs.length > 0) {
				queues[key].myJobs[0].isRunning = true;
			}
		}
		this.jobQueues = queues;
		this.emit("jobs-changed");
	}

	registerQueuedJob(job:Job):void{
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		if (queue.myJobs.some(j=>j.getID() == job.getID())) return;
		queue.myJobs.push(job);
	}

	async removeJobFromQueue(job: Job): Promise<void> {
		await Axios.delete(`${this.url}/${job.UserID}/${job.Name}/${job.SubQueue}`);
	}

	async cancelJob(job:Job):Promise<void>{
		await Axios.post(`${this.url}/${job.UserID}/cancel-current/${job.SubQueue}`);
		job.ShouldCancel = true;
	}

	async unCancelCurrent(job:Job):Promise<void>{
		await Axios.post(`${this.url}/${job.UserID}/un-cancel-current/${job.SubQueue}`);
		job.ShouldCancel = false;
	}

	private onStatusMessageUpdate(data:any) {
		let job = new Job(data);
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		let j = queue.myJobs.find(j=>j.getID() == job.getID());
		if (!j) {
			queue.myJobs.push(job);
			return;
		}
		j.StatusMessage = job.StatusMessage;
		j.StatusMessageData = job.StatusMessageData;
	}

	private onProgressUpdate(data:any) {
		let job = new Job(data);
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		let j = queue.myJobs.find(j=>j.getID() == job.getID());
		if (!j) {
			queue.myJobs.push(job);
			return;
		}
		j.Progress = job.Progress;
		j.ProgressMaxParts = job.ProgressMaxParts;
		j.ProgressPart = job.ProgressPart;
	}

	private onJobCancel(data:any){
		let job = new Job(data);
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		let j = queue.myJobs.find(j=>j.getID() == job.getID());
		if (!j) {
			return;
		}
		j.ShouldCancel = true;
	}
	private onJobUnCancel(data:any){
		let job = new Job(data);
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		let j = queue.myJobs.find(j=>j.getID() == job.getID());
		if (!j) {
			return;
		}
		j.ShouldCancel = false;
	}

	private onJobStart(data:any){
		let job = new Job(data);
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		let index = queue.myJobs.findIndex(j => j.getID() == job.getID());
		if (index != -1) {
			queue.myJobs[index].isRunning = true;
		} else {
			job.isRunning = true;
			index = queue.myJobs.push(job) -1;
		}
		this.emit(`job-started/${job.getID()}`, queue.myJobs[index]);
	}

	private onJobStop(data:any){
		let job = new Job(data);
		let queue = this.jobQueues[job.SubQueue];
		if (!queue) return;
		let index = queue.myJobs.findIndex(j => j.getID() == job.getID());
		if (index != -1) {
			queue.myJobs.splice(index, 1);
			this.emit(`job-stopped/${job.getID()}`, job);
			this.emit("job-stopped", job);
		}
		// De gedeelde wachtrijteller krijgt niet altijd een socketbericht bij het stoppen; opnieuw ophalen houdt hem juist.
		this.fetchJobs().catch(() => {});
	}

	public getJobs():Job[]{
		let keys = Object.keys(this.jobQueues);
		let result:Job[] = [];
		for (let key of keys){
			result.push(...this.jobQueues[key].myJobs);
		}
		return result;
	}

	public getQueuedJobs():number{
		let keys = Object.keys(this.jobQueues);
		let result = 0;
		for (let key of keys){
			result += this.jobQueues[key].queuedJobs;
		}
		return result;
	}

	init(data:IInitAppResponse) {
		if (!this.initialized) {
			AuthService.on("logout", () => {
				this.jobQueues = {};
			});

			MainSocketService.on(`${this.socketUrl}queue`, (data:{Count:number, QueueName:string}) => {
				let queue = this.jobQueues[data.QueueName];
				if (!queue) return;
				queue.queuedJobs = data.Count;
			});

			MainSocketService.on(this.socketUrl + "queued", (data: any) => {
				this.registerQueuedJob(new Job(data));
			});
			this.initialized = true;
			MainSocketService.on(this.socketUrl + "start", this.onJobStart.bind(this));
			MainSocketService.on(this.socketUrl + "stop", this.onJobStop.bind(this));
			MainSocketService.on(this.socketUrl + "status-message", this.onStatusMessageUpdate.bind(this));
			MainSocketService.on(this.socketUrl + "progress", this.onProgressUpdate.bind(this));
			MainSocketService.on(this.socketUrl + "canncel", this.onJobCancel.bind(this));
			MainSocketService.on(this.socketUrl + "uncanncel", this.onJobUnCancel.bind(this));
		}

		this.jobQueues = data.JobQueues;
		this.emit("jobs-changed");
	}
};

export const JobService = new JobServiceClass();
